import type {
  AlertsReport,
  DocumentDetails,
  DocumentEntityType,
  DocumentKind,
  DocumentRecord,
  Paginated,
} from '@ldembalagens/types';
import { api, apiFetch, downloadFile, type QueryParams } from '@/lib/api-client';
import { multipart } from '@/features/invoices/invoices-api';

export interface DocumentQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  kind?: DocumentKind;
  entityType?: DocumentEntityType;
  entityId?: string;
  tag?: string;
  expiringInDays?: number;
  includeInactive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DocumentMetaInput {
  title: string;
  description?: string | null;
  kind?: DocumentKind;
  tags?: string[];
  issuedAt?: string | null;
  expiresAt?: string | null;
  entityType?: DocumentEntityType;
  entityId?: string | null;
  confidential?: boolean;
  status?: 'ACTIVE' | 'INACTIVE';
}

const metaFields = (m: DocumentMetaInput): Record<string, string | undefined> => ({
  title: m.title,
  description: m.description ?? undefined,
  kind: m.kind,
  tags: m.tags ? JSON.stringify(m.tags) : undefined,
  issuedAt: m.issuedAt ?? undefined,
  expiresAt: m.expiresAt ?? undefined,
  entityType: m.entityType,
  entityId: m.entityId ?? undefined,
  confidential: m.confidential === undefined ? undefined : String(m.confidential),
});

export const documentsApi = {
  list: (q: DocumentQuery) => api.get<Paginated<DocumentRecord>>('/documents', q),
  get: (id: string) => api.get<DocumentDetails>(`/documents/${id}`),
  create: (meta: DocumentMetaInput, file: File) =>
    apiFetch<DocumentDetails>('/documents', {
      method: 'POST',
      body: multipart(file, metaFields(meta)),
    }),
  addVersion: (id: string, file: File) =>
    apiFetch<DocumentDetails>(`/documents/${id}/versions`, {
      method: 'POST',
      body: multipart(file),
    }),
  update: (id: string, meta: Partial<DocumentMetaInput>) =>
    api.patch<DocumentDetails>(`/documents/${id}`, meta),
  remove: (id: string) => api.delete(`/documents/${id}`),
  /** Download autorizado e auditado (segue o redirecionamento assinado no S3 ou recebe o arquivo). */
  download: (id: string, version?: number) =>
    downloadFile(`/documents/${id}/download`, version ? { version } : undefined),
  alerts: () => api.get<AlertsReport>('/alerts'),
};

export const documentsKeys = {
  all: ['documents'] as const,
  list: (q: object) => ['documents', 'list', q] as const,
  detail: (id: string) => ['documents', 'detail', id] as const,
  alerts: ['alerts'] as const,
};
